import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Card, CardContent, Button, Eyebrow, RaceBib } from '@/components/foundry';
import { cn } from '@/lib/utils';

/**
 * LifestyleTargetCalculator — Foundations Module 02 · Lesson 01 ("The life you
 * want"). A hosted interactive Labs tool: work backwards from the freedom you
 * want (take-home income, time) to the revenue your business needs.
 *
 * Foundry light. Race-yellow is accent chrome only (plate, button, marker
 * underline, slider fill); the hero number stays ink for contrast on the warm
 * canvas. Writes a planning-anchor artifact (confidence-tagged) to the profile.
 */

type PersonaKey = 'fitness' | 'consultant' | 'course' | 'community';

const PERSONAS: Record<PersonaKey, { label: string; floor: number; hrs: number; off: number }> = {
  fitness:    { label: 'Fitness coach',    floor: 90_000,  hrs: 20, off: 6 },
  consultant: { label: 'Consultant',       floor: 180_000, hrs: 30, off: 5 },
  course:     { label: 'Course creator',   floor: 120_000, hrs: 15, off: 8 },
  community:  { label: 'Community owner',   floor: 120_000, hrs: 25, off: 6 },
};

const usd = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
const usdK = (n: number) =>
  n >= 1000 ? '$' + Math.round(n / 1000).toLocaleString('en-US') + 'k' : '$' + Math.round(n);
const parseMoney = (s: string) => Number(String(s).replace(/[^0-9.]/g, '')) || 0;

const LS_KEY = 'abl:labs:lifestyle-target';

async function captureLead(payload: Record<string, unknown>) {
  // Integration seam: POST to the leads endpoint (Resend email + member match).
  // Best-effort — the public-tool UX completes even if the endpoint isn't wired.
  try {
    await fetch('/api/labs/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    /* no-op: lead capture is best-effort in v1 */
  }
}

/** rAF count-up that tweens from whatever is currently shown (handles rapid input). */
function useCountUp(target: number): number {
  const [val, setVal] = useState(0);
  const valRef = useRef(0);
  const rafRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const from = valRef.current;
    const to = target;
    const dur = from === 0 ? 1100 : 600;
    const t0 = performance.now();
    const ease = (x: number) => 1 - Math.pow(1 - x, 3);
    const tick = (now: number) => {
      const p = Math.min((now - t0) / dur, 1);
      const v = from + (to - from) * ease(p);
      valRef.current = v;
      setVal(v);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else {
        valRef.current = to;
        setVal(to);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target]);
  return val;
}

function ConfTag({ kind, children }: { kind: 'self' | 'anchor' | 'computed'; children: ReactNode }) {
  const tone =
    kind === 'self'
      ? 'text-fd-teal border-fd-teal/30 bg-fd-teal/10'
      : kind === 'anchor'
        ? 'text-fd-accent-deep border-fd-accent/30 bg-fd-accent/10'
        : 'text-fd-text-muted border-fd-border bg-fd-surface-1';
  return (
    <span
      className={cn(
        'inline-block rounded-[6px] border px-2 py-[3px] font-geist-mono text-[9.5px] font-medium uppercase tracking-[0.03em]',
        tone,
      )}
    >
      {children}
    </span>
  );
}

function Slider({
  id,
  min,
  max,
  value,
  onChange,
}: {
  id: string;
  min: number;
  max: number;
  value: number;
  onChange: (n: number) => void;
}) {
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <input
      id={id}
      type="range"
      min={min}
      max={max}
      step={1}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ '--fill': `${fill}%` } as CSSProperties}
      className="lt-range"
    />
  );
}

interface Props {
  /** 'profile' = "Save to my profile" (member, in-OS). 'email' = capture an
   *  email and send the result (public Labs surface). */
  saveMode?: 'profile' | 'email';
}

export default function LifestyleTargetCalculator({ saveMode = 'profile' }: Props) {
  const [persona, setPersona] = useState<PersonaKey>('fitness');
  const [floorStr, setFloorStr] = useState('90,000');
  const [hrs, setHrs] = useState(20);
  const [off, setOff] = useState(6);
  const [tax, setTax] = useState(30);
  const [ov, setOv] = useState(25);
  const [saved, setSaved] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [email, setEmail] = useState('');
  const [emailDone, setEmailDone] = useState(false);

  // Restore + persist inputs so a returning visitor keeps their numbers
  // (no account needed). localStorage, best-effort.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(LS_KEY);
      if (!raw) return;
      const s = JSON.parse(raw) as Record<string, unknown>;
      if (typeof s.persona === 'string' && s.persona in PERSONAS) setPersona(s.persona as PersonaKey);
      if (typeof s.floorStr === 'string') setFloorStr(s.floorStr);
      if (typeof s.hrs === 'number') setHrs(s.hrs);
      if (typeof s.off === 'number') setOff(s.off);
      if (typeof s.tax === 'number') setTax(s.tax);
      if (typeof s.ov === 'number') setOv(s.ov);
    } catch {
      /* ignore corrupt/blocked storage */
    }
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify({ persona, floorStr, hrs, off, tax, ov }));
    } catch {
      /* ignore */
    }
  }, [persona, floorStr, hrs, off, tax, ov]);

  const floor = parseMoney(floorStr);

  const r = useMemo(() => {
    const grossPersonal = floor / (1 - tax / 100);
    const revenue = grossPersonal / (1 - ov / 100);
    const weeks = 52 - off;
    const annualHours = Math.max(weeks * hrs, 1);
    return {
      revenue,
      monthly: revenue / 12,
      weeks,
      perHour: revenue / annualHours,
      lo: revenue * 0.8,
      hi: revenue * 1.2,
    };
  }, [floor, hrs, off, tax, ov]);

  const shownRevenue = useCountUp(r.revenue);

  function applyPersona(key: PersonaKey) {
    const p = PERSONAS[key];
    setPersona(key);
    setFloorStr(p.floor.toLocaleString('en-US'));
    setHrs(p.hrs);
    setOff(p.off);
    setTax(30);
    setOv(25);
    setSaved(false);
  }
  function dirty() {
    if (saved) setSaved(false);
  }

  return (
    <div className="lt-calc">
      <style>{`
        .lt-calc .lt-range{-webkit-appearance:none;appearance:none;width:100%;height:3px;border-radius:3px;background:var(--fd-surface-2);outline:0;margin:10px 0 0;cursor:pointer}
        .lt-calc .lt-range::-webkit-slider-runnable-track{height:3px;border-radius:3px;background:linear-gradient(to right,var(--fd-accent-deep) var(--fill,40%),var(--fd-surface-2) var(--fill,40%))}
        .lt-calc .lt-range::-moz-range-progress{height:3px;border-radius:3px;background:var(--fd-accent-deep)}
        .lt-calc .lt-range::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;background:var(--fd-paper);border:2px solid var(--fd-accent-deep);border-radius:50%;margin-top:-7px;box-shadow:0 1px 2px rgba(20,16,8,0.18)}
        .lt-calc .lt-range::-moz-range-thumb{width:16px;height:16px;background:var(--fd-paper);border:2px solid var(--fd-accent-deep);border-radius:50%}
      `}</style>

      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="max-w-[60ch]">
          <div className="mb-4 flex items-center gap-3">
            <RaceBib n="01" tone="race" size="lg" shadow="sm" className="-rotate-3" />
            <Eyebrow>Module 02 · Lesson 01</Eyebrow>
          </div>
          <h1 className="font-geist text-[32px] font-medium leading-[1.05] tracking-[-0.022em] text-fd-text">
            The life you want.
          </h1>
          <p className="mt-3 text-[15px] leading-[1.55] text-fd-text-muted">
            Start from the freedom you actually want: your time, and the income that pays for it.
            Then we work backwards to the revenue your business needs to make it real.
          </p>
        </div>
        {imgOk && (
          <div className="relative hidden w-[150px] shrink-0 sm:block">
            <span className="absolute -top-1 right-0 font-caveat text-[20px] leading-none text-fd-teal">
              your numbers ↘
            </span>
            {/* Generated per prompt pack — docs/content/labs-tools/lifestyle-target-calculator-image-prompt-pack.md */}
            <img
              src="/labs-tools/lifestyle-target-builder-v1.png"
              alt="Someone working out their numbers on paper"
              className="mt-5 block w-full select-none"
              draggable={false}
              onError={() => setImgOk(false)}
            />
          </div>
        )}
      </div>

      <div className="my-7 h-px w-full bg-fd-border/70" />

      {/* persona */}
      <Eyebrow className="mb-3 block">Start from where you are</Eyebrow>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(PERSONAS) as PersonaKey[]).map((key) => {
          const active = key === persona;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={active}
              onClick={() => applyPersona(key)}
              className={cn(
                'rounded-fd-sm border px-[15px] py-[9px] font-geist-mono text-[11px] font-medium uppercase tracking-[0.04em] transition-colors',
                active
                  ? 'border-fd-accent-deep bg-fd-accent text-fd-text'
                  : 'border-fd-border bg-fd-paper text-fd-text-muted hover:border-fd-text hover:text-fd-text',
              )}
            >
              {PERSONAS[key].label}
            </button>
          );
        })}
      </div>

      {/* grid */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.08fr)]">
        {/* INPUTS */}
        <Card>
          <CardContent className="pt-[22px]">
            <div className="mb-5 flex items-center justify-between">
              <Eyebrow>Your inputs</Eyebrow>
              <Eyebrow className="text-fd-text-subtle">Editable</Eyebrow>
            </div>

            <div className="mb-[22px]">
              <div className="mb-[9px] flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-medium text-fd-text">Income you want to take home</span>
                <ConfTag kind="self">Self-reported</ConfTag>
              </div>
              <div className="flex items-center rounded-fd border border-fd-border bg-fd-surface-1 focus-within:border-fd-text">
                <span className="pl-[15px] pr-1 font-geist-mono text-[24px] font-medium text-fd-text-subtle">$</span>
                <input
                  inputMode="numeric"
                  value={floorStr}
                  onChange={(e) => {
                    setFloorStr(e.target.value);
                    dirty();
                  }}
                  onBlur={(e) => {
                    const n = parseMoney(e.target.value);
                    setFloorStr(n ? n.toLocaleString('en-US') : '');
                  }}
                  aria-label="Annual take-home income"
                  className="w-full bg-transparent px-1 py-[11px] font-geist-mono text-[27px] font-medium tabular-nums tracking-[-0.01em] text-fd-text outline-none"
                />
              </div>
              <p className="mt-2 text-[12.5px] text-fd-text-subtle">After tax, in your pocket, per year.</p>
            </div>

            <div className="mb-[22px]">
              <div className="mb-[3px] flex items-baseline justify-between">
                <span className="text-[13px] font-medium text-fd-text">Hours you want to work / week</span>
                <span className="font-geist-mono text-[16px] font-medium tabular-nums text-fd-text">{hrs}</span>
              </div>
              <Slider id="hrs" min={5} max={50} value={hrs} onChange={(n) => { setHrs(n); dirty(); }} />
            </div>

            <div>
              <div className="mb-[3px] flex items-baseline justify-between">
                <span className="text-[13px] font-medium text-fd-text">Weeks off / year</span>
                <span className="font-geist-mono text-[16px] font-medium tabular-nums text-fd-text">{off}</span>
              </div>
              <Slider id="off" min={0} max={20} value={off} onChange={(n) => { setOff(n); dirty(); }} />
            </div>
          </CardContent>
        </Card>

        {/* RESULT */}
        <Card>
          <CardContent className="pt-[22px]">
            <Eyebrow className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-fd-accent shadow-[0_0_0_3px_var(--fd-accent-soft)]" />
              Revenue your business needs
            </Eyebrow>

            <div className="mt-2 w-fit">
              <div className="font-geist-mono text-[58px] font-semibold leading-none tracking-[-0.03em] tabular-nums text-fd-text">
                {usd(shownRevenue)}
              </div>
              <svg viewBox="0 0 300 9" preserveAspectRatio="none" className="-mt-0.5 block h-[9px] w-full">
                <path d="M2 6 C 70 2, 150 8, 298 4" stroke="var(--fd-accent)" strokeWidth={4} fill="none" strokeLinecap="round" />
              </svg>
            </div>
            <p className="mt-2 text-[14px] text-fd-text-muted">
              per year &nbsp;·&nbsp;{' '}
              <b className="font-geist-mono font-semibold text-fd-text">{usd(r.monthly)}</b> / mo
            </p>

            <div className="mt-6 grid grid-cols-3 overflow-hidden rounded-fd border border-fd-border/70">
              <div className="border-r border-fd-border/70 px-4 py-[15px]">
                <div className="font-geist-mono text-[9.5px] uppercase tracking-[0.05em] text-fd-text-muted">Working weeks</div>
                <div className="mt-1.5 font-geist-mono text-[23px] font-semibold tabular-nums text-fd-text">{r.weeks}</div>
              </div>
              <div className="border-r border-fd-border/70 px-4 py-[15px]">
                <div className="font-geist-mono text-[9.5px] uppercase tracking-[0.05em] text-fd-text-muted">Your time is worth</div>
                <div className="mt-1.5 font-geist-mono text-[23px] font-semibold tabular-nums text-fd-text">
                  {usd(r.perHour)}<small className="text-[12px] font-medium text-fd-text-subtle">/hr</small>
                </div>
              </div>
              <div className="px-4 py-[15px]">
                <div className="font-geist-mono text-[9.5px] uppercase tracking-[0.05em] text-fd-text-muted">Confidence</div>
                <div className="mt-2"><ConfTag kind="computed">Computed</ConfTag></div>
              </div>
            </div>

            <div className="mt-[22px]">
              <div className="mb-[11px] flex items-baseline justify-between">
                <Eyebrow>If your number moves ±20%</Eyebrow>
                <Eyebrow className="text-fd-text-subtle">Sensitivity</Eyebrow>
              </div>
              <div className="relative h-2 rounded-[5px] bg-fd-surface-2">
                <div className="absolute inset-y-0 left-[18%] right-[18%] rounded-[5px] bg-fd-teal/15" />
                <div className="absolute -inset-y-[3px] left-1/2 w-[3px] -translate-x-1/2 rounded-[2px] bg-fd-teal" />
              </div>
              <div className="mt-[9px] flex justify-between font-geist-mono text-[11px] text-fd-text-muted">
                <span>{usdK(r.lo)}</span>
                <span className="text-fd-teal">target</span>
                <span>{usdK(r.hi)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* assumptions */}
      <Card className="mt-5">
        <CardContent className="pt-[22px]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <Eyebrow>The assumptions behind the math</Eyebrow>
            <ConfTag kind="anchor">Industry anchor · adjust</ConfTag>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
            <div>
              <div className="mb-[3px] flex items-baseline justify-between">
                <span className="text-[13px] font-medium text-fd-text">Tax set-aside</span>
                <span className="font-geist-mono text-[16px] font-medium tabular-nums text-fd-text">{tax}%</span>
              </div>
              <Slider id="tax" min={0} max={50} value={tax} onChange={(n) => { setTax(n); dirty(); }} />
              <p className="mt-2 text-[12.5px] text-fd-text-subtle">What you keep aside for tax before it hits your pocket.</p>
            </div>
            <div>
              <div className="mb-[3px] flex items-baseline justify-between">
                <span className="text-[13px] font-medium text-fd-text">Delivery &amp; overhead</span>
                <span className="font-geist-mono text-[16px] font-medium tabular-nums text-fd-text">{ov}%</span>
              </div>
              <Slider id="ov" min={0} max={60} value={ov} onChange={(n) => { setOv(n); dirty(); }} />
              <p className="mt-2 text-[12.5px] text-fd-text-subtle">
                Tools, contractors, ads-as-cost, fulfilment: the share of revenue spent to deliver.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* foot */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <p className="flex max-w-[54ch] items-start gap-2 text-[12.5px] text-fd-text-subtle">
          <span aria-hidden className="mt-px font-geist-mono">ⓘ</span>
          <span>
            This is a planning anchor, not a forecast. Your AI co-founder sizes every recommendation
            against it, and flags when reality drifts. You can edit it anytime.
          </span>
        </p>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => applyPersona(persona)}>Reset</Button>
          {saveMode === 'email' ? (
            emailDone ? (
              <Button variant="outline" className="border-fd-teal/40 bg-fd-teal/10 text-fd-teal" disabled>
                Sent — check your inbox
              </Button>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
                  void captureLead({
                    tool: 'lifestyle-target',
                    email,
                    inputs: { floor, hrs, off, tax, ov },
                    result: { revenue: r.revenue, monthly: r.monthly, perHour: r.perHour },
                  });
                  setEmailDone(true);
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  aria-label="Email to send your plan"
                  className="h-8 rounded-fd-sm border border-fd-border bg-fd-paper px-3 font-geist text-[13px] text-fd-text outline-none focus:border-fd-text"
                />
                <Button type="submit" variant="primary">Email me my plan</Button>
              </form>
            )
          ) : saved ? (
            <Button variant="outline" className="border-fd-teal/40 bg-fd-teal/10 text-fd-teal" disabled>
              Saved to your profile
            </Button>
          ) : (
            <Button variant="primary" onClick={() => setSaved(true)}>Save to my profile</Button>
          )}
        </div>
      </div>
    </div>
  );
}
